import { useMemo, useRef } from 'react';

import { toAssignmentsPayload } from '../session/mappingModel';
import type { MappingAssignments } from '../session/types';
import { useDebouncedValue } from './useDebouncedValue';

export const useDebouncedMappingDraft = (
  draft: MappingAssignments | null,
  wait: number,
  maxWait: number,
): {
  assignments: ReturnType<typeof toAssignmentsPayload> | null;
  isDraftInFlight: boolean;
} => {
  const debouncedDraft = useDebouncedValue(draft, {
    wait,
    maxWait,
    // A brush release is its own burst, usually more than `wait` after the
    // previous one. A leading edge would dispatch that release immediately.
    leading: false,
  });

  // The hook is mounted before mapping mode, so the debounced value starts
  // null and the first real draft would otherwise wait out `wait` too. Keep
  // that opening draft, and let only later edits go through the quiet period.
  const openingDraftRef = useRef(draft);
  if (draft === null) {
    openingDraftRef.current = null;
  } else if (openingDraftRef.current === null) {
    openingDraftRef.current = draft;
  }
  const settledDraft = debouncedDraft ?? openingDraftRef.current;

  const assignments = useMemo(
    () => (settledDraft ? toAssignmentsPayload(settledDraft) : null),
    [settledDraft],
  );

  return { assignments, isDraftInFlight: draft !== settledDraft };
};
